import { z } from 'zod';

/** Resolución normalizada. El orden de este array ES el orden de calidad:
 *  nunca se ordena por tamaño, porque un 2160p moderno pesa menos que un 1080p antiguo. */
export const RESOLUTION_LADDER = ['480p', '576p', '720p', '1080p', '2160p'] as const;
export const resolutionSchema = z.enum(RESOLUTION_LADDER);
export type Resolution = z.infer<typeof resolutionSchema>;

export function compareResolution(a: Resolution | null, b: Resolution | null): number {
  const ia = a ? RESOLUTION_LADDER.indexOf(a) : -1;
  const ib = b ? RESOLUTION_LADDER.indexOf(b) : -1;
  return ia - ib;
}

/** La etiqueta de resolución que le corresponde a un vídeo por sus píxeles: el peldaño de
 *  `RESOLUTION_LADDER` más cercano al marco 16:9 en el que cabe.
 *
 *  Se mira el ancho además del alto porque un 1920×800 —cine en 2,40:1, con las bandas ya
 *  recortadas— es un 1080p, no un «800p»: lo que lo describe es el marco, no la altura de
 *  la imagen. Es la misma regla que usa D-24 para decidir qué se reescala. Sin dimensiones
 *  no hay etiqueta. */
export function resolucionDeDimensiones(
  ancho: number | null | undefined,
  alto: number | null | undefined,
): Resolution | null {
  const altoDelMarco = ancho ? Math.round((ancho * 9) / 16) : 0;
  const nominal = Math.max(alto ?? 0, altoDelMarco);
  if (nominal <= 0) return null;
  let mejor: Resolution | null = null;
  let distancia = Number.POSITIVE_INFINITY;
  for (const r of RESOLUTION_LADDER) {
    const d = Math.abs(Number.parseInt(r, 10) - nominal);
    // En empate se queda el peldaño de abajo: no se le atribuye a un fichero más de lo que tiene.
    if (d < distancia) {
      mejor = r;
      distancia = d;
    }
  }
  return mejor;
}

/** Qué es una obra del catálogo: una película o una serie (D-27).
 *
 *  Es una columna de `movie` y no una tabla aparte porque HispaShare sirve una serie con la
 *  misma ficha, los mismos grupos y la misma identidad que una película, y aquí releases,
 *  ficheros, descargas y conversiones cuelgan todos de esa fila. La diferencia entre las dos
 *  está en los ficheros —una película es uno; una serie, uno por episodio— y eso ya vive en
 *  `season` y `episode` de cada fichero. */
export const titleKindSchema = z.enum(['movie', 'show']);
export type TitleKind = z.infer<typeof titleKindSchema>;

/** Un grupo de Elinks puede mezclar tipos: vídeo troceado en `.avi` junto a `.srt` y `.rar`.
 *  Por eso la clasificación es de cada fichero, no del release. */
export const fileKindSchema = z.enum(['video', 'subtitles', 'archive', 'other']);
export type FileKind = z.infer<typeof fileKindSchema>;

export const downloadStatusSchema = z.enum([
  'queued',
  'downloading',
  'paused',
  /** aMule está hasheando el fichero: o rehasheando un parcial al arrancar, o terminando
   *  una descarga (su `completing`). Con 6 GB son minutos, y con la cola entera rehasheándose
   *  tras un reinicio fueron 45. Es un estado propio porque los dos vecinos mienten: «En
   *  cola» parece que no ha empezado y «Completada» parece que el fichero ya está. */
  'hashing',
  'completed',
  'transcoding',
  'transcode_failed',
  /** Convertida y esperando en la carpeta de conversiones: falta elegir biblioteca. */
  'ready_to_place',
  'importing',
  'imported',
  'import_failed',
  'failed',
  'cancelled',
]);
export type DownloadStatus = z.infer<typeof downloadStatusSchema>;

/** `exact` es cruce por IMDb; `fuzzy` es por título y año. La interfaz DEBE distinguirlos:
 *  un `fuzzy` se pinta como "puede que ya la tengas", nunca como un hecho. */
export const matchConfidenceSchema = z.enum(['exact', 'fuzzy']);
export type MatchConfidence = z.infer<typeof matchConfidenceSchema>;

export const libraryStatusSchema = z.object({
  status: z.enum(['owned', 'missing', 'unknown']),
  confidence: matchConfidenceSchema.nullable(),
  resolution: z.string().nullable(),
  serverName: z.string().nullable(),
  libraryTitle: z.string().nullable(),
  plexRatingKey: z.string().nullable(),
  upgradeAvailable: z.boolean(),
  /** Cuántos episodios de esta serie hay en Plex, cuando la obra es una serie (D-27). Nulo
   *  en una película. «Tenerla» en una serie es una cuenta, no un sí o un no: la ficha dice
   *  episodio a episodio cuáles están. */
  episodiosEnPlex: z.number().int().nullable().default(null),
});
export type LibraryStatus = z.infer<typeof libraryStatusSchema>;

export const titleSummarySchema = z.object({
  id: z.string(),
  kind: titleKindSchema,
  title: z.string(),
  originalTitle: z.string().nullable(),
  year: z.number().int().nullable(),
  imdbId: z.string().nullable(),
  posterUrl: z.string().nullable(),
  imdbRating: z.number().nullable(),
  director: z.string().nullable(),
  library: libraryStatusSchema,
});
export type TitleSummary = z.infer<typeof titleSummarySchema>;

export const releaseFileSchema = z.object({
  id: z.string(),
  position: z.number().int(),
  fileName: z.string(),
  sizeBytes: z.number().int().nullable(),
  kind: fileKindSchema,
  season: z.number().int().nullable(),
  episode: z.number().int().nullable(),
  /** Hasta qué episodio llega cuando el fichero trae varios seguidos (D-27). */
  episodeEnd: z.number().int().nullable(),
});
export type ReleaseFile = z.infer<typeof releaseFileSchema>;

export const releaseSchema = z.object({
  id: z.string(),
  elinkId: z.number().int(),
  qualityRaw: z.string().nullable(),
  resolution: resolutionSchema.nullable(),
  formatRaw: z.string().nullable(),
  /** Las tres piezas de `formatRaw` ya separadas (`mkv (x265-EAC3)`). Viajan aparte del
   *  texto crudo porque son lo que se compara contra las preferencias: el contenedor y el
   *  códec deciden si una versión vale, y sacarlos de la cadena en el navegador sería
   *  repetir aquí el parser que ya vive en el adaptador. */
  container: z.string().nullable(),
  videoCodec: z.string().nullable(),
  audioCodec: z.string().nullable(),
  audioLangs: z.array(z.string()),
  subtitleLangs: z.array(z.string()),
  /** Subtítulos forzados: traducen solo lo que ya venía en otro idioma. No son una
   *  subtitulación de la película, y por eso viajan aparte de `subtitleLangs`. */
  forcedSubLangs: z.array(z.string()),
  ripper: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
  downloadCount: z.number().int().nullable(),
  postedAt: z.string().nullable(),
  files: z.array(releaseFileSchema),
});
export type Release = z.infer<typeof releaseSchema>;

const texto = z.string().trim().min(1).max(120);
const anio = z.number().int().min(1880).max(2200);

/** Los filtros de la búsqueda avanzada de HispaShare (`?view=advsearch`), todos opcionales.
 *
 *  Vive en el paquete compartido porque los valida la API y los construye el formulario del
 *  navegador: una sola definición evita que el día que el sitio admita un filtro más haya que
 *  acordarse de tocar dos sitios. La correspondencia con los `adv*` del sitio está en
 *  `construirUrlBusqueda`, que es el único que conoce sus nombres. */
export const busquedaAvanzadaSchema = z.object({
  titulo: texto.optional(),
  anioMin: anio.optional(),
  anioMax: anio.optional(),
  pais: texto.optional(),
  categorias: z.array(texto).max(10).optional(),
  generos: z.array(texto).max(20).optional(),
  duracionMin: z.number().int().min(0).max(2000).optional(),
  duracionMax: z.number().int().min(0).max(2000).optional(),
  director: texto.optional(),
  actor: texto.optional(),
  sinopsis: texto.optional(),
  ripper: texto.optional(),
  origen: texto.optional(),
  formato: texto.optional(),
  video: texto.optional(),
  audio: texto.optional(),
  calidad: texto.optional(),
  notaMin: z.number().min(0).max(10).optional(),
  notaMax: z.number().min(0).max(10).optional(),
  /** Empieza en 1. Pedir una página que no existe devuelve la última, no una lista vacía:
   *  quien pagina tiene que parar por los ids repetidos, no por la respuesta. */
  pagina: z.number().int().min(1).max(200).optional(),
});
export type BusquedaAvanzada = z.infer<typeof busquedaAvanzadaSchema>;
